import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Table, Button, Spinner } from "react-bootstrap";
import parse from "html-react-parser";
import { useAppContext } from "../../contexts/AppContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { isImage, isVideo } from "../MediaFilter";
import thumbnailFallback from "../../assets/thumbnail.jpg";


const AdminDataTable = ({ apiEndPoint, columns, title, onEdit, reorderable }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const { getImageUrl } = useAppContext();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(apiEndPoint);
      if (Array.isArray(response.data)) {
        setData(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error(`Error fetching ${title}:`, error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [apiEndPoint, title]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounced search logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!searchTerm) {
        setFilteredData(data);
        return;
      }

      const filtered = data.filter((item) => {
        return Object.values(item).some((val) => 
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredData(filtered);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, data]);

  const handleDelete = async (id) => {
    if (window.confirm(`Are you sure you want to delete this ${title.toLowerCase()}?`)) {
      try {
        await axios.delete(`${apiEndPoint}/${id}`);
        alert("Deleted successfully");
        fetchData();
      } catch (error) {
        console.error("Error deleting:", error);
        alert("Failed to delete");
      }
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(data);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UI
    setData(items);

    // Update orders based on new positions
    const updatedItems = items.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));

    try {
      await axios.put(`${apiEndPoint}/reorder`, { items: updatedItems });
    } catch (error) {
      console.error("Error syncing new order:", error);
      alert("Failed to save new order");
      fetchData(); // Rollback to server state
    }
  };

  const renderCellContent = (item, col) => {
    const value = item[col.key];
    
    if (col.type === "image") {
      const imgPath = Array.isArray(value) ? value[0] : value;
      const fullUrl = getImageUrl(imgPath);
      return imgPath ? (
        isImage(fullUrl) ? (
          <img 
            src={fullUrl} 
            alt="Thumbnail" 
            style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }} 
          />
        ) : isVideo(fullUrl) ? (
          <img 
            src={thumbnailFallback} 
            alt="Video Thumbnail" 
            style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }} 
          />
        ) : (
          <div style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", borderRadius: "4px" }}>
            <i className="bi bi-file-earmark-play"></i>
          </div>
        )
      ) : "No Media";
    }

    if (col.type === "html") {
      return value ? (
        <div 
          className="admin-table-html-cell" 
          style={{ 
            maxWidth: "200px", 
            maxHeight: "80px", 
            overflow: "hidden", 
            fontSize: "13px"
          }}
        >
          {parse(value)}
        </div>
      ) : "-";
    }

    if (typeof value === "boolean") {
      return value ? (
        <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-3 py-2">
          <i className="bi bi-check-circle-fill me-1"></i> Active
        </span>
      ) : (
        <span className="badge rounded-pill bg-danger-subtle text-danger border border-danger-subtle px-3 py-2">
          <i className="bi bi-x-circle-fill me-1"></i> Inactive
        </span>
      );
    }

    return value ? (
      <div 
        className="text-truncate" 
        style={{ maxWidth: "200px" }}
      >
        {String(value)}
      </div>
    ) : "-";
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <h5 className="fw-bold m-0">{title} List</h5>
        
        <div className="d-flex gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ width: "250px" }}>
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={fetchData} 
            style={{ borderColor: "var(--primary-color)", color: "var(--primary-color)" }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <Table hover className="align-middle border-light">
          <thead className="table-light">
            <tr>
              {reorderable && <th style={{ width: "40px" }}></th>}
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="admin-table-body" isDropDisabled={!reorderable}>
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <Draggable 
                        key={item.id.toString()} 
                        draggableId={item.id.toString()} 
                        index={index}
                        isDragDisabled={!reorderable}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "table-active shadow-sm" : ""}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging ? "#f8f9fa" : "inherit"
                            }}
                          >
                            {reorderable && (
                              <td {...provided.dragHandleProps} className="text-muted">
                                <i className="bi bi-grip-vertical fs-5"></i>
                              </td>
                            )}
                            {columns.map((col) => (
                              <td key={`${item.id}-${col.key}`}>
                                {renderCellContent(item, col)}
                              </td>
                            ))}
                            <td className="text-end">
                              <div className="d-flex justify-content-end gap-2 flex-wrap">
                                <button 
                                  className="btn-action edit"
                                  onClick={() => onEdit(item)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil-fill"></i>
                                </button>
                                <button 
                                  className="btn-action delete"
                                  onClick={() => handleDelete(item.id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash3-fill"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length + (reorderable ? 2 : 1)} className="text-center py-4 text-muted">
                        No data found
                      </td>
                    </tr>
                  )}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      </div>
      <style>{`
        .btn-action {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          background: #f8fafc;
        }
        .btn-action.edit {
          color: var(--primary-color);
        }
        .btn-action.edit:hover {
          background: var(--primary-color);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(255, 165, 0, 0.2);
        }
        .btn-action.delete {
          color: #ef4444;
        }
        .btn-action.delete:hover {
          background: #ef4444;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
        }
        .admin-table-html-cell img {
          max-width: 50px;
          height: auto;
        }
      `}</style>
    </div>
  );
};

export default AdminDataTable;
